/**
 * Aircraft Buffered Interpolation
 *
 * Renders aircraft positions by interpolating between known past samples
 * rather than extrapolating into the future. The render head is held a
 * fixed `RENDER_DELAY_MS` behind real time so we always have at least one
 * "future" sample to interpolate toward, eliminating the prediction error
 * (and resulting forward / backward slides) that an extrapolation-based
 * approach can never fully avoid.
 *
 * Interpolation curve: Catmull-Rom through 4 surrounding control points.
 * C¹ continuous, so velocity has no discontinuities at sample boundaries —
 * the world view in cockpit POV scrolls past at a perfectly steady rate.
 *
 * Heading: derived from the Catmull-Rom velocity vector at the render time,
 * so the camera always points along the actual direction of travel. Falls
 * back to interpolated reported heading when motion is too small to derive
 * a stable angle (taxiing, hovering, briefly stationary).
 *
 * Trade-off: rendered positions are 2.5 s behind real time. For an
 * Australian-bushfire monitoring tool that surfaces aerial firefighting
 * activity, that's invisible — nothing actionable depends on sub-2.5 s
 * latency to ADS-B updates. The smoothness win is the entire reason the
 * cockpit / wing camera modes exist.
 *
 * Design influences: this is the same approach that production flight
 * simulators and game engines use for network-driven object rendering
 * (Source engine's "interp", Counter-Strike's lag compensation, etc.).
 */

import type { AircraftState } from '../types/events';

// ============================================================================
// Constants
// ============================================================================

const EARTH_RADIUS_M = 6_371_000;
const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;

/** How far behind real time the render head sits, in ms. Must be greater
 *  than the worst-case interval between server samples — otherwise the
 *  render head walks past the last buffered sample between polls, falls
 *  into the dead-reckon fallback, and switches back to interpolation when
 *  the next sample arrives. That mode flip happens every poll cycle and
 *  is itself a stutter. With a ~5 s polling interval (occasionally jittery
 *  due to anneal / network), 6000 ms keeps us safely inside the buffer
 *  the entire time so the smooth curve is the only path. */
const RENDER_DELAY_MS = 6000;

/** Mark aircraft as stale if no server update for this long (ms) */
const STALE_THRESHOLD_MS = 30_000;

/** Remove aircraft entirely if no update for this long (ms) */
const REMOVAL_THRESHOLD_MS = 60_000;

/** Maximum extrapolation horizon when the render head is past the last
 *  sample (e.g. server is silent or aircraft just appeared). After this
 *  we freeze to the last known position rather than predict forward. */
const MAX_EXTRAPOLATION_MS = 15_000;

/** Maximum samples kept in the per-aircraft buffer. 60 entries × ~5 s
 *  spacing = ~5 minutes of history, which is comfortably more than any
 *  trail rendering or interpolation horizon needs. */
const MAX_BUFFER_LENGTH = 60;

// ============================================================================
// Types
// ============================================================================

export interface InterpolatedAircraft extends AircraftState {
  /** Smoothly interpolated latitude for rendering */
  interpLat: number;
  /** Smoothly interpolated longitude for rendering */
  interpLon: number;
  /** Smoothly interpolated altitude (meters) for rendering */
  interpAltitude: number | null;
  /** Smoothly interpolated heading (degrees) for rendering */
  interpHeading: number;
  /** Whether this aircraft has stale data */
  isStale: boolean;
  /** Whether this aircraft is being tracked (cockpit mode) */
  isTracked: boolean;
}

/** One sample in the per-aircraft replay buffer. Keeps the data needed
 *  for both Catmull-Rom interpolation and the fallback dead-reckon used
 *  when the render head is past the latest sample. */
interface BufferEntry {
  lat: number;
  lon: number;
  alt: number | null;
  heading: number | null;
  velocityMs: number | null;
  verticalRateMs: number | null;
  /** performance.now() timestamp when this sample arrived from the server. */
  time: number;
}

/** Internal tracking state for each aircraft */
export interface AircraftTrackingState {
  /** Most recent server data (used by callers that read non-position
   *  attributes like callsign, registration, emergency flag). */
  aircraft: AircraftState;
  /** performance.now() of the most recent server update (used for
   *  staleness / removal decisions, not for interpolation). */
  lastServerUpdateTime: number;
  /** Replay buffer of past samples; interpolation is done by walking
   *  this buffer at `now − RENDER_DELAY_MS`. */
  positionHistory: BufferEntry[];
}

// ============================================================================
// Math helpers
// ============================================================================

/**
 * Time-aware Hermite cubic interpolation between p1 and p2.
 *
 * Endpoint velocities are derived from the surrounding control points
 * p0 and p3 using real-time differences (NOT just neighbouring values),
 * which is what makes velocity continuous across sample boundaries even
 * when polling intervals are non-uniform.
 *
 * Why not standard Catmull-Rom: the textbook formula uses uniform t ∈ [0,1]
 * over each segment regardless of segment duration. With server samples
 * arriving at non-uniform intervals (5 s polling + jitter + anneal), this
 * produces a velocity discontinuity at every sample boundary, which shows
 * up visually as the aircraft "slowing, pulling back slightly, then
 * accelerating again" — overshoot followed by correction. Time-aware
 * Hermite eliminates that by parametrising velocity in actual seconds.
 *
 * @param p0,p1,p2,p3   control point values
 * @param t0,t1,t2,t3   their timestamps in ms
 * @param tNow          render timestamp; expected p1.time ≤ tNow ≤ p2.time
 */
function timeAwareHermite(
  p0: number, p1: number, p2: number, p3: number,
  t0: number, t1: number, t2: number, t3: number,
  tNow: number
): number {
  const segDuration = t2 - t1;
  if (segDuration <= 0) return p1;

  // Endpoint velocities in value-units per ms of real time. At each
  // boundary the segment's own velocity matches the neighbouring
  // segment's velocity (both compute from the same shared (p3-p1)/(t3-t1)
  // expression on either side), giving C¹ continuity in real time.
  const v1 = (p2 - p0) / Math.max(1, t2 - t0);
  const v2 = (p3 - p1) / Math.max(1, t3 - t1);

  // Hermite tangents in segment-local s ∈ [0, 1] units.
  const m1 = v1 * segDuration;
  const m2 = v2 * segDuration;

  const s = (tNow - t1) / segDuration;
  const s2 = s * s;
  const s3 = s2 * s;

  return (
    (2 * s3 - 3 * s2 + 1) * p1 +
    (s3 - 2 * s2 + s) * m1 +
    (-2 * s3 + 3 * s2) * p2 +
    (s3 - s2) * m2
  );
}

/** Time-aware Hermite across angles in degrees, with shortest-arc
 *  unwrapping so the curve doesn't sweep through 359° to wrap from 1° to
 *  360°. */
function hermiteAngle(
  p0: number, p1: number, p2: number, p3: number,
  t0: number, t1: number, t2: number, t3: number,
  tNow: number
): number {
  // Anchor everything to p1; unwrap p0/p2/p3 to be in (-180..+180) of p1.
  const u0 = p1 + shortestAngleDelta(p1, p0);
  const u2 = p1 + shortestAngleDelta(p1, p2);
  const u3 = u2 + shortestAngleDelta(u2, p3);
  const result = timeAwareHermite(u0, p1, u2, u3, t0, t1, t2, t3, tNow);
  return ((result % 360) + 360) % 360;
}

/** Shortest signed angular delta from `from` to `to`, in (-180, 180]. */
function shortestAngleDelta(from: number, to: number): number {
  return ((to - from) % 360 + 540) % 360 - 180;
}

/** Predict a position from a starting point using velocity and heading.
 *  Used only for the fallback path when the render head is past the last
 *  buffered sample (server is silent / aircraft just appeared). */
function deadReckonPosition(
  lat: number,
  lon: number,
  velocityMs: number,
  headingDeg: number,
  dtSeconds: number
): { lat: number; lon: number } {
  const distance = velocityMs * dtSeconds;
  const headingRad = headingDeg * DEG_TO_RAD;
  const dNorth = distance * Math.cos(headingRad);
  const dEast = distance * Math.sin(headingRad);
  const dLat = (dNorth / EARTH_RADIUS_M) * RAD_TO_DEG;
  const dLon = (dEast / (EARTH_RADIUS_M * Math.cos(lat * DEG_TO_RAD))) * RAD_TO_DEG;
  return { lat: lat + dLat, lon: lon + dLon };
}

/** Predict altitude from current altitude and vertical rate. */
function deadReckonAltitude(
  altitude: number | null,
  verticalRateMs: number | null,
  dtSeconds: number
): number | null {
  if (altitude === null) return null;
  if (verticalRateMs === null || verticalRateMs === 0) return altitude;
  return Math.max(0, altitude + verticalRateMs * dtSeconds);
}

// ============================================================================
// AircraftTracker
// ============================================================================

/**
 * AircraftTracker manages the per-aircraft replay buffer and serves
 * interpolated positions at `now − RENDER_DELAY_MS`.
 *
 * Usage:
 *   const tracker = new AircraftTracker();
 *
 *   // On every server update (every ~5 s):
 *   tracker.updateFromServer(aircraftFromServer);
 *
 *   // On every animation frame (60 fps):
 *   const interpolated = tracker.getInterpolatedPositions();
 *   for (const ac of interpolated) renderAircraft(ac);
 */
export class AircraftTracker {
  private states: Map<string, AircraftTrackingState> = new Map();
  private trackedAircraftId: string | null = null;

  /** Update all aircraft with new server data. Called when new data
   *  arrives from the CloudFront-cached ADS-B feed (every ~5 s). */
  updateFromServer(aircraft: AircraftState[]): void {
    const now = performance.now();
    const receivedIds = new Set<string>();

    for (const ac of aircraft) {
      if (ac.latitude === null || ac.longitude === null) continue;

      receivedIds.add(ac.icao24);
      const existing = this.states.get(ac.icao24);

      const entry: BufferEntry = {
        lat: ac.latitude,
        lon: ac.longitude,
        alt: ac.altitude_m,
        heading: ac.heading,
        velocityMs: ac.velocity_ms,
        verticalRateMs: ac.vertical_rate,
        time: now,
      };

      if (existing) {
        existing.aircraft = ac;
        existing.lastServerUpdateTime = now;
        existing.positionHistory.push(entry);
        while (existing.positionHistory.length > MAX_BUFFER_LENGTH) {
          existing.positionHistory.shift();
        }
      } else {
        this.states.set(ac.icao24, {
          aircraft: ac,
          lastServerUpdateTime: now,
          positionHistory: [entry],
        });
      }
    }

    // Remove aircraft that haven't been seen in a long time
    for (const [id, state] of this.states) {
      if (!receivedIds.has(id)) {
        const age = now - state.lastServerUpdateTime;
        if (age > REMOVAL_THRESHOLD_MS) {
          this.states.delete(id);
          if (this.trackedAircraftId === id) {
            this.trackedAircraftId = null;
          }
        }
      }
    }
  }

  /** Get interpolated positions for all known aircraft. Call from the
   *  per-frame animation loop. */
  getInterpolatedPositions(): InterpolatedAircraft[] {
    const now = performance.now();
    const results: InterpolatedAircraft[] = [];

    for (const [id, state] of this.states) {
      const age = now - state.lastServerUpdateTime;
      if (age > REMOVAL_THRESHOLD_MS) continue;

      const interp = this.interpolateSingle(state, now);
      results.push({
        ...state.aircraft,
        interpLat: interp.lat,
        interpLon: interp.lon,
        interpAltitude: interp.altitude,
        interpHeading: interp.heading,
        isStale: age > STALE_THRESHOLD_MS,
        isTracked: id === this.trackedAircraftId,
      });
    }

    return results;
  }

  /** Interpolate one aircraft's position at the render time
   *  (now − RENDER_DELAY_MS). */
  private interpolateSingle(
    state: AircraftTrackingState,
    now: number
  ): { lat: number; lon: number; altitude: number | null; heading: number } {
    const buffer = state.positionHistory;

    // Defensive: empty buffer shouldn't happen but handle it.
    if (buffer.length === 0) {
      return { lat: 0, lon: 0, altitude: null, heading: 0 };
    }

    // Single sample — no curve to interpolate, just render the point.
    if (buffer.length === 1) {
      const e = buffer[0];
      return { lat: e.lat, lon: e.lon, altitude: e.alt, heading: e.heading ?? 0 };
    }

    const renderTime = now - RENDER_DELAY_MS;

    // Render head is before the first sample we ever received — clamp.
    if (renderTime <= buffer[0].time) {
      const e = buffer[0];
      return { lat: e.lat, lon: e.lon, altitude: e.alt, heading: e.heading ?? 0 };
    }

    // Render head is past the last sample — server is silent OR the
    // aircraft only just appeared and we don't yet have a "future" sample
    // to interpolate toward. Fall back to a short dead-reckon forward
    // from the most recent point. After MAX_EXTRAPOLATION_MS we freeze.
    const lastIdx = buffer.length - 1;
    const last = buffer[lastIdx];
    if (renderTime >= last.time) {
      const dt = (renderTime - last.time) / 1000;
      const onGround = state.aircraft.on_ground === true;
      if (
        dt > MAX_EXTRAPOLATION_MS / 1000 ||
        onGround ||
        last.velocityMs === null ||
        last.heading === null
      ) {
        return { lat: last.lat, lon: last.lon, altitude: last.alt, heading: last.heading ?? 0 };
      }
      const predicted = deadReckonPosition(last.lat, last.lon, last.velocityMs, last.heading, dt);
      return {
        lat: predicted.lat,
        lon: predicted.lon,
        altitude: deadReckonAltitude(last.alt, last.verticalRateMs, dt),
        heading: last.heading,
      };
    }

    // Find the bracket that contains renderTime: buffer[i] <= renderTime
    // < buffer[i+1]. Buffer is sorted by time (append-only).
    let i = 0;
    for (let j = 0; j < buffer.length - 1; j++) {
      if (buffer[j].time <= renderTime && buffer[j + 1].time > renderTime) {
        i = j;
        break;
      }
    }

    const p1 = buffer[i];
    const p2 = buffer[i + 1];
    const p0 = i > 0 ? buffer[i - 1] : p1;
    const p3 = i + 2 < buffer.length ? buffer[i + 2] : p2;

    // Time-aware Hermite cubic. Use REAL timestamps so the velocity in
    // real time is continuous across sample boundaries — fixes the
    // "slow, pull back, accelerate" overshoot the user reported when
    // poll intervals were non-uniform under uniform Catmull-Rom.
    const lat = timeAwareHermite(
      p0.lat, p1.lat, p2.lat, p3.lat,
      p0.time, p1.time, p2.time, p3.time,
      renderTime
    );
    const lon = timeAwareHermite(
      p0.lon, p1.lon, p2.lon, p3.lon,
      p0.time, p1.time, p2.time, p3.time,
      renderTime
    );

    // Altitude: only interpolate if the bracket has it; tolerate missing
    // p0/p3 by reusing p1/p2 as their tangent neighbours.
    let altitude: number | null = null;
    if (p1.alt !== null && p2.alt !== null) {
      const a0 = p0.alt ?? p1.alt;
      const a3 = p3.alt ?? p2.alt;
      altitude = timeAwareHermite(
        a0, p1.alt, p2.alt, a3,
        p0.time, p1.time, p2.time, p3.time,
        renderTime
      );
    }

    // Heading: shortest-arc time-aware Hermite of reported server
    // headings. Reported heading is the actual aircraft direction logged
    // by ADS-B — initially I tried deriving heading from the Hermite
    // velocity vector but with sparse / annealed samples the curve can
    // briefly tangent perpendicular or even reversed during sharp turns,
    // producing visible 180° flips. Reported heading never inverts.
    const h0 = p0.heading ?? p1.heading ?? 0;
    const h1 = p1.heading ?? 0;
    const h2 = p2.heading ?? h1;
    const h3 = p3.heading ?? h2;
    const heading = hermiteAngle(
      h0, h1, h2, h3,
      p0.time, p1.time, p2.time, p3.time,
      renderTime
    );

    return { lat, lon, altitude, heading };
  }

  /** Set which aircraft to track (cockpit mode). null to stop tracking. */
  setTrackedAircraft(icao24: string | null): void {
    this.trackedAircraftId = icao24;
  }

  /** Get the currently tracked aircraft ID. */
  getTrackedAircraftId(): string | null {
    return this.trackedAircraftId;
  }

  /** Get the tracked aircraft's current interpolated position. */
  getTrackedPosition(): InterpolatedAircraft | null {
    if (!this.trackedAircraftId) return null;
    const state = this.states.get(this.trackedAircraftId);
    if (!state) return null;

    const now = performance.now();
    const age = now - state.lastServerUpdateTime;
    if (age > REMOVAL_THRESHOLD_MS) return null;

    const interp = this.interpolateSingle(state, now);
    return {
      ...state.aircraft,
      interpLat: interp.lat,
      interpLon: interp.lon,
      interpAltitude: interp.altitude,
      interpHeading: interp.heading,
      isStale: age > STALE_THRESHOLD_MS,
      isTracked: true,
    };
  }

  /** Position history for trail rendering. Public consumers only read
   *  lat/lon/time, so the richer BufferEntry shape is upward-compatible. */
  getPositionHistory(icao24: string): Array<{ lat: number; lon: number; time: number }> {
    return this.states.get(icao24)?.positionHistory ?? [];
  }

  /** Look up a specific aircraft by ICAO24 hex code. */
  getAircraftById(icao24: string): InterpolatedAircraft | null {
    const state = this.states.get(icao24);
    if (!state) return null;

    const now = performance.now();
    const age = now - state.lastServerUpdateTime;
    if (age > REMOVAL_THRESHOLD_MS) return null;

    const interp = this.interpolateSingle(state, now);
    return {
      ...state.aircraft,
      interpLat: interp.lat,
      interpLon: interp.lon,
      interpAltitude: interp.altitude,
      interpHeading: interp.heading,
      isStale: age > STALE_THRESHOLD_MS,
      isTracked: icao24 === this.trackedAircraftId,
    };
  }

  /** Number of currently tracked aircraft. */
  getAircraftCount(): number {
    return this.states.size;
  }

  /** Clear all tracking state. */
  clear(): void {
    this.states.clear();
    this.trackedAircraftId = null;
  }
}
